import { isPlainObject, isArray, isNil, isEqual, mapValues, isObject } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import filterTypes, { FiltersMap } from './types';
import type { UseFiltersQuery, FiltersFormData } from './Filters';

const defaultValues = mapValues(filterTypes, 'default');

function getValue(valObj: any) {
  return isPlainObject(valObj) ? valObj.value : valObj;
}

function isSameDate(d1: Date, d2: Date) {
  return dayjs(d1).isSame(dayjs(d2), 'date');
}

function convertToQP(
  setQuery: UseFiltersQuery['setQuery'],
  data: FiltersFormData,
  oldQuery: UseFiltersQuery['query'],
  { defaults = false, updateQuery = true } = {},
) {
  const timeUnit = 'day';

  const query: UseFiltersQuery['query'] = {
    filters: {},
    fromDate: undefined,
    toDate: undefined,
    phrase: undefined,
    sums: undefined,
    // groups: undefined,
  };
  const apiHeaders = {};
  const apiParams: any = {};

  const { dateRange, phrase, filters = {} } = data;
  const [fromMoment, to] = dateRange ?? [];
  const toMoment = to && to.clone().endOf(timeUnit);

  if (fromMoment && toMoment) {
    query.fromDate = fromMoment.toDate();
    query.toDate = toMoment.toDate();

    apiParams.fromDate = fromMoment.toDate().toISOString();
    apiParams.toDate = toMoment.toDate().toISOString();
  }

  if (phrase) {
    query.phrase = phrase;
    apiParams.phrase = phrase;
  }

  const { filters: oldFilters = {} } = oldQuery;
  let updateFiltersQP = false;

  Object.entries(filters).forEach(([key, val]) => {
    let value = getValue(val);

    if (isNil(value)) {
      if (oldFilters[key] !== undefined) {
        query.filters![key] = undefined;
        updateFiltersQP = true;
      }
      return;
    }

    if (isArray(value)) {
      value = value.map(getValue).filter(Boolean);

      if (!value.length) {
        if (oldFilters[key] !== undefined) {
          query.filters[key] = undefined;
          updateFiltersQP = true;
          query.filters![key] = undefined;
        }
        return;
      }
    }

    query.filters[key] = value;
    if (!isEqual(oldFilters[key], value)) updateFiltersQP = true;

    query.filters![key] = value;
    apiParams[key] = value;
  });

  if (Object.values(query.filters).filter((v) => !isNil(v)).length === 0) {
    query.filters = undefined;
  }
  if (oldQuery.sums) {
    query.sums = oldQuery.sums;
    apiParams.sums = oldQuery.sums;
  }
  // if (oldQuery.groups) {
  //   query.groups = oldQuery.groups;
  //   apiParams.groups = oldQuery.groups;
  // }

  if (
    updateQuery &&
    query.fromDate &&
    query.toDate &&
    (updateFiltersQP ||
      !oldQuery.fromDate ||
      !oldQuery.toDate ||
      !isSameDate(query.fromDate, oldQuery.fromDate) ||
      !isSameDate(query.toDate, oldQuery.toDate))
  ) {
    setQuery(
      { fromDate: query.fromDate, toDate: query.toDate, filters: query.filters, sums: query.sums },
      defaults ? 'replaceIn' : 'pushIn',
    );
  }

  return { apiParams, apiHeaders };
}

function convertFromQP(query: UseFiltersQuery['query'], filtersMap: FiltersMap) {
  const { fromDate, toDate, phrase } = query;
  const filters = query.filters ?? {};
  const dateFilter = filtersMap.date;
  const useDefaultTodayRange =
    !(isObject(dateFilter) && 'oldFromDate' in dateFilter && dateFilter.oldFromDate === false);

  const fromMoment = fromDate
    ? dayjs(fromDate).startOf('day')
    : useDefaultTodayRange
      ? dayjs().startOf('day')
      : undefined;
  const toMoment = toDate
    ? dayjs(toDate).endOf('day')
    : useDefaultTodayRange
      ? dayjs().endOf('day')
      : undefined;

  const data: { filters: PlainObject<unknown>; dateRange?: [Dayjs, Dayjs]; phrase?: string } = {
    filters: {},
    ...(fromMoment && toMoment && { dateRange: [fromMoment, toMoment] }),
    ...(phrase && { phrase }),
  };

  Object.entries(filtersMap).forEach(([key, val]) => {
    if (['date', 'phrase'].includes(key) || !val) return;
    const path = isObject(val) && 'name' in val && val.name ? val.name : key;

    if (filters[path]) {
      if (path === 'publishDate') {
        data.filters[path] = filters.publishDate[1]
          ? [dayjs(filters.publishDate[0]), dayjs(filters.publishDate[1])]
          : [dayjs(filters.publishDate[0])];
      }
      else {
        data.filters[path] = !Number.isNaN(Date.parse(filters[path]))
          ? dayjs(filters[path])
          : filters[path];
      }
    }
    else {
      data.filters[path] =
        isObject(val) && 'initialValue' in val && val.initialValue
          ? val.initialValue
          : defaultValues[key];
    }
  });
  return data;
}

export { convertToQP, convertFromQP };
